import {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslationApp } from "../../lib/i18next";
import Loader from "../Loaders/Loader";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import Typography from "@mui/material/Typography";
import { TableColumnLayoutContext } from "./tableColumnLayoutContext";
import {
  EMPTY_RESULTS_SHOW,
  LOADING_INFORMATION,
  PAGE_IN_TABLE,
  RECORDS_IN_TABLE,
} from "../../i18n/keysTranslations";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import IconButton from "@mui/material/IconButton";
import TableContainerMUI from "@mui/material/TableContainer";
import {
  BOX_SHADOW_THEME,
  TABLE_COLOR_BORDER_HEADER,
} from "../../utils/colors";
import { BORDER_RADIUS, IS_ADLE_DOMAIN } from "../../utils/constants";
import { AdleLogoLoader } from "../../assets/svg/AdleLogoLoader";

const sameWidths = (prev, next) =>
  Array.isArray(prev) &&
  Array.isArray(next) &&
  prev.length === next.length &&
  prev.every((width, index) => width === next[index]);

const TableContainer = ({
  children,
  elevation = 3,
  page,
  onPageChange,
  keys = [],
  rowsPerPage,
  ariaLabel = "",
  hasPagination = true,
  sx,
  additionalValidationNext = false,
  additionalValidationEmpty = false,
  loading = false,
  loadingText = LOADING_INFORMATION,
  emptyText = EMPTY_RESULTS_SHOW,
  isFiltering = false,
  loaderType = "circular",
  loaderTime = 30,
  customComponentBottomLeft = null,
  rounded = true,
  sxContainer = {},
  stickyFooter = null,
  showEmptyText = true,
  inheritColumnLayout = false,
  stickyHeader = true,
  minTableWidth = 450,
  scrollContainerRef = null,
}) => {
  const { t } = useTranslationApp();
  const parentLayout = useContext(TableColumnLayoutContext);
  const [columnWidths, setColumnWidths] = useState(null);
  const [headerWidths, setHeaderWidths] = useState(null);
  const [contentMinWidth, setContentMinWidth] = useState(minTableWidth);
  const columnWidthsRef = useRef(null);
  const tableRef = useRef(null);

  const lockColumnWidths = useCallback((widths) => {
    columnWidthsRef.current = widths;
    setColumnWidths(widths);
  }, []);

  const setColumnWidthAt = useCallback((index, width, minWidth = 130) => {
    setColumnWidths((prev) => {
      const base = prev ?? columnWidthsRef.current;
      if (!base || index < 0 || index >= base.length) return prev;
      const nextWidth = Math.max(minWidth, Math.round(width));
      if (base[index] === nextWidth) return prev ?? base;
      const next = [...base];
      next[index] = nextWidth;
      columnWidthsRef.current = next;
      return next;
    });
  }, []);

  const inheritedWidths = inheritColumnLayout
    ? (parentLayout.measuredWidths ?? parentLayout.widths)
    : null;
  const appliedWidths = inheritedWidths ?? columnWidths;

  const columnLayout = useMemo(() => {
    if (inheritedWidths?.length) {
      return {
        ...parentLayout,
        widths: inheritedWidths,
        measuredWidths: inheritedWidths,
      };
    }
    return {
      widths: columnWidths,
      measuredWidths: headerWidths,
      lockColumnWidths,
      setColumnWidthAt,
    };
  }, [
    inheritedWidths,
    parentLayout,
    columnWidths,
    headerWidths,
    lockColumnWidths,
    setColumnWidthAt,
  ]);

  const appliedTableMinWidth = appliedWidths?.reduce(
    (sum, width) => sum + width,
    0
  );

  useLayoutEffect(() => {
    if (inheritColumnLayout) return undefined;
    const table = tableRef.current;
    if (!table) return undefined;
    const headerRow = table.querySelector("thead tr");
    if (!headerRow) return undefined;

    const measure = () => {
      const headers = table.querySelectorAll(
        "thead tr:first-child th, thead tr:first-child td"
      );
      if (!headers.length) return;
      const headerCount = headers.length;
      if (columnWidths?.length && columnWidths.length !== headerCount) {
        lockColumnWidths(null);
        return;
      }
      const nextHeaderWidths = Array.from(headers).map((header) =>
        Math.round(header.getBoundingClientRect().width)
      );
      setHeaderWidths((prev) =>
        sameWidths(prev, nextHeaderWidths) ? prev : nextHeaderWidths
      );
      if (columnWidths) return;
      let sum = 0;
      headers.forEach((header) => {
        const min = Number.parseFloat(getComputedStyle(header).minWidth);
        sum += Number.isFinite(min) ? min : 0;
      });
      const next = Math.max(Math.round(sum), minTableWidth);
      setContentMinWidth((prev) => (prev === next ? prev : next));
    };

    measure();
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(headerRow, { childList: true });
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(table);
    headerRow.querySelectorAll("th, td").forEach((header) => {
      resizeObserver.observe(header);
    });
    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [columnWidths, inheritColumnLayout, lockColumnWidths, minTableWidth]);

  const getCountRecords = ({ page, rowsPerPage, keys }) => {
    if (keys === 0) return 0;
    var firstRecord = (page - 1) * rowsPerPage + 1;
    var lastRecord = Math.min(page * rowsPerPage, keys);
    return firstRecord + "-" + lastRecord;
  };

  if (loading && loaderType === "circular")
    return (
      <Loader
        size={40}
        fullWidth
        hasMessage={true}
        message={t(loadingText)}
        variantMessage="body1"
      />
    );

  if (loading && loaderType === "linear") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        {IS_ADLE_DOMAIN && <AdleLogoLoader />}
        <LinearLoaderWithTime label={t(loadingText)} time={loaderTime * 1000} />
      </Box>
    );
  }

  if (
    (keys.length === 0 || additionalValidationEmpty) &&
    !loading &&
    showEmptyText
  )
    return (
      <Typography variant="body1" align="center">
        {t(isFiltering ? EMPTY_RESULTS_SHOW : emptyText)}
      </Typography>
    );
  return (
    <TableColumnLayoutContext.Provider value={columnLayout}>
      <Box
        sx={{
          width: inheritColumnLayout ? "100%" : 0,
          minWidth: inheritColumnLayout ? 0 : "100%",
          maxWidth: "100%",
          alignSelf: "stretch",
          boxShadow: inheritColumnLayout ? "none" : BOX_SHADOW_THEME,
          borderRadius: rounded
            ? `${BORDER_RADIUS * 4}px !important`
            : "0px !important",
          ...sxContainer,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            overflow: inheritColumnLayout ? "visible" : "hidden",
            borderRadius: rounded
              ? `${BORDER_RADIUS * 4}px !important`
              : "0px !important",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TableContainerMUI
            ref={scrollContainerRef}
            component={Paper}
            elevation={inheritColumnLayout ? 0 : elevation}
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflowX: inheritColumnLayout ? "hidden" : "auto",
              backgroundColor: inheritColumnLayout
                ? "transparent"
                : "background.paper",
              backgroundImage: inheritColumnLayout
                ? "none"
                : (theme) =>
                    `linear-gradient(${theme.palette?.table?.header?.background || "#F9FAFB"} 54px, ${TABLE_COLOR_BORDER_HEADER} 54px, ${TABLE_COLOR_BORDER_HEADER} 55px, transparent 55px)`,
              backgroundRepeat: "no-repeat",
              boxShadow: inheritColumnLayout ? "none" : undefined,
              borderRadius: rounded
                ? `${BORDER_RADIUS * 4}px !important`
                : "0px !important",
              ...sx,
            }}
          >
            <Table
              ref={tableRef}
              stickyHeader={stickyHeader}
              sx={{
                width: appliedWidths?.length ? appliedTableMinWidth : "100%",
                minWidth: appliedWidths?.length
                  ? appliedTableMinWidth
                  : inheritColumnLayout
                    ? "100%"
                    : Math.max(contentMinWidth, minTableWidth),
                tableLayout: appliedWidths ? "fixed" : "auto",
                backgroundColor: "transparent",
              }}
              aria-label={ariaLabel}
            >
              {appliedWidths?.length > 0 && (
                <colgroup>
                  {appliedWidths.map((width, index) => (
                    <col key={index} style={{ width }} />
                  ))}
                </colgroup>
              )}
              {children}
            </Table>
          </TableContainerMUI>
          {stickyFooter && (
            <Box
              sx={{
                backgroundColor: "background.paper",
                borderTop: "1px solid rgba(224, 224, 224, 1)",
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                overflow: "hidden",
                "& .MuiTable-root": {
                  backgroundColor: "background.paper",
                },
              }}
            >
              {stickyFooter}
            </Box>
          )}
          {hasPagination && (
            <Paper
              sx={{
                borderRadius: 0,
                backgroundColor: "background.paper",
                backgroundImage: "none",
                width: "100%",
                display: "flex",
                justifyContent: customComponentBottomLeft
                  ? "space-between"
                  : "flex-end",
                alignItems: "center",
                gap: 1,
              }}
              elevation={1}
            >
              {customComponentBottomLeft && customComponentBottomLeft}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">{`${getCountRecords({
                  page: page + 1,
                  rowsPerPage,
                  keys: keys.length,
                })} ${t(RECORDS_IN_TABLE)}`}</Typography>
                <Typography variant="body2">{`${t(PAGE_IN_TABLE)} N°${
                  page + 1
                }`}</Typography>
                <Box>
                  <IconButton
                    disabled={page === 0 || loading}
                    onClick={() => onPageChange(page - 1)}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => onPageChange(page + 1)}
                    disabled={
                      keys.length <= rowsPerPage * (page + 1) ||
                      additionalValidationNext ||
                      loading
                    }
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
    </TableColumnLayoutContext.Provider>
  );
};

export default TableContainer;
